import { useInfiniteQuery } from '@tanstack/react-query'
import {
  Card,
  Empty,
  Flex,
  Form,
  FormListFieldData,
  InputNumber,
  Select,
  Spin,
  Table,
  TableColumnsType
} from 'antd'
import { FormInstance } from 'antd/lib'
import { useTheme } from 'antd-style'
import { useMemo, useState } from 'react'

import {
  useStocksServiceGetConsumableListEndpointKey,
  useStocksServiceGetMedicineListEndpointKey
} from 'src/api/queries'
import {
  GetConsumableListResponse,
  GetMedicineListResponse,
  StocksService
} from 'src/api/requests'
import { DEFAULT_PER_PAGE, requiredRules } from 'src/app-constants'
import { EditableFormItem, SubmitButtons, Text } from 'src/components'
import {
  INVESTIGATION_MEDICINE_FORM,
  RECIPES_CONSUMABLES_FIELDS
} from 'src/features/persons/utils/constants'
import { useDebounce } from 'src/hooks'
import { useLocales } from 'src/locales'

type Props = {
  fields: FormListFieldData[]
  form?: FormInstance
  isFinished?: boolean
  remove: (index: number) => void
}

const generateFields = (medicines: { name?: string }[]) =>
  medicines
    .map((_, index: number) =>
      RECIPES_CONSUMABLES_FIELDS.map((field) => [
        INVESTIGATION_MEDICINE_FORM,
        index,
        field
      ])
    )
    .flat()

export const InvestigationConsumablesList = ({
  fields,
  form,
  remove,
  isFinished
}: Props) => {
  const [search, setSearch] = useState('')

  const [editingIndex, setEditingIndex] = useState<number>()
  const [isNewConsumable, setIsNewConsumable] = useState(false)

  const debouncedSearch = useDebounce(search)
  const { t } = useLocales()

  const palette = useTheme()

  const {
    data: medicinesData,
    isFetching: isFetchingMedicines,
    fetchNextPage: fetchNextMedicinesPage,
    hasNextPage: hasNextMedicinesPage
  } = useInfiniteQuery({
    queryKey: [useStocksServiceGetMedicineListEndpointKey, debouncedSearch],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response = await StocksService.getMedicineListEndpoint(
        pageParam,
        DEFAULT_PER_PAGE,
        debouncedSearch
      )

      return {
        data: response.data,
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data?: GetMedicineListResponse[]
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1
  })

  const {
    data: consumablesData,
    fetchNextPage: fetchNextConsumablesPage,
    isFetching: isFetchingConsumables,
    hasNextPage: hasNextConsumablesPage
  } = useInfiniteQuery({
    queryKey: [useStocksServiceGetConsumableListEndpointKey, debouncedSearch],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response = await StocksService.getConsumableListEndpoint(
        pageParam,
        DEFAULT_PER_PAGE,
        debouncedSearch
      )

      return {
        data: response.data,
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data?: GetConsumableListResponse[]
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1
  })

  const combinedData = useMemo(() => {
    const medicines = medicinesData?.pages.flatMap((page) => page.data) || []
    const consumables =
      consumablesData?.pages.flatMap((page) => page.data) || []

    return [
      ...medicines.map((item) => ({
        label: `${item?.internationalName?.name} ( ${t('stock_applications.series')}: ${item?.code} - ${t('common.producer')}: ${item?.producer?.name} )`,
        value: item?.id,
        item
      })),
      ...consumables.map((item) => ({
        label: `${item?.internationalName?.name} ( ${t('common.producer')}: ${item?.producer?.name} )`,
        value: item?.id,
        item
      }))
    ]
  }, [medicinesData, consumablesData])

  const list = Form.useWatch('investigationMedicine', form) || []

  const filteredCombinedData = useMemo(() => {
    const selectedIds = new Set(list.map((item: { id: number }) => item.id))

    return combinedData.filter((data) => !selectedIds.has(data.value))
  }, [combinedData, list])

  const onCancel = (index: number) => {
    if (isNewConsumable) {
      remove(index)
    } else {
      form?.resetFields(
        RECIPES_CONSUMABLES_FIELDS.map((field) => [
          INVESTIGATION_MEDICINE_FORM,
          index,
          field
        ])
      )
    }

    setIsNewConsumable(false)
    setEditingIndex(undefined)
  }

  const onSelect = (value: string) => {
    const selectedItem = combinedData.find((item) => item.value === value)

    if (selectedItem) {
      const newInquiryItem = {
        name: selectedItem.label,
        id: selectedItem.value,
        internationalNameId: selectedItem?.item?.internationalName?.id,
        productFormName: selectedItem?.item?.form?.name,
        productFormId: selectedItem?.item?.form?.id,
        item: selectedItem.item
      }

      form?.setFieldsValue({
        investigationMedicine: [...list, newInquiryItem]
      })
      setSearch('')
      setEditingIndex(list.length)
      setIsNewConsumable(true)
    }
  }

  const onSave = async () => {
    const medicines = form?.getFieldValue(INVESTIGATION_MEDICINE_FORM)

    const fieldsToValidate = generateFields(medicines)

    const values = await form?.validateFields(fieldsToValidate)

    if (values) {
      setIsNewConsumable(false)
      setEditingIndex(undefined)
      form?.setFieldsValue({
        [INVESTIGATION_MEDICINE_FORM]: values?.investigationMedicine
      })
    }
  }

  const columns: TableColumnsType = useMemo(
    () => [
      {
        key: 'name',
        title: t('consultations.preparation_name'),
        render: (_, __, index) => {
          const um = form?.getFieldValue([
            INVESTIGATION_MEDICINE_FORM,
            index,
            'name'
          ])

          return <Text>{um}</Text>
        }
      },

      {
        key: 'um',
        title: t('common.unit'),
        render: (_, __, index) => {
          const um = form?.getFieldValue([
            INVESTIGATION_MEDICINE_FORM,
            index,
            'productFormName'
          ])

          return <Text>{um}</Text>
        }
      },
      {
        key: 'dispensedAmount',
        minWidth: 200,
        title: t('common.quantity'),
        render: (_, __, index) => {
          return (
            <EditableFormItem
              name={[index, 'dispensedAmount']}
              isEditing={editingIndex === index}
              rules={requiredRules}
            >
              <InputNumber
                min={1}
                placeholder={t('common.quantity')}
                className="full-width"
              />
            </EditableFormItem>
          )
        }
      },
      {
        width: 105,
        fixed: 'right' as const,
        key: 'actions',
        render: (_: unknown, __: unknown, index: number) => (
          <SubmitButtons
            editingIndex={editingIndex}
            isEditing={editingIndex === index}
            onSave={onSave}
            onCancel={() => onCancel(index)}
            onEdit={() => setEditingIndex(index)}
            onDelete={() => remove(index)}
          />
        )
      }
    ],
    [t, palette]
  )

  return (
    <>
      <Table
        dataSource={fields}
        rowKey={(record) => JSON.stringify(record)}
        scroll={{ x: 'max-content' }}
        columns={columns}
        pagination={false}
      />

      {!isFinished && (
        <Card variant="borderless">
          <Select
            allowClear
            showSearch
            value={null}
            onSearch={(value) => setSearch(value)}
            onClear={() => setSearch('')}
            loading={isFetchingConsumables || isFetchingMedicines}
            options={filteredCombinedData}
            filterOption={false}
            onSelect={(value) => value && onSelect(value)}
            placeholder={t('common.search')}
            onPopupScroll={(e) => {
              const target = e.target as HTMLElement
              if (
                target.scrollTop + target.offsetHeight ===
                target.scrollHeight
              ) {
                hasNextMedicinesPage && fetchNextMedicinesPage()
                hasNextConsumablesPage && fetchNextConsumablesPage()
              }
            }}
            notFoundContent={
              isFetchingConsumables || isFetchingMedicines ? (
                <Flex justify="center">
                  <Spin spinning />
                </Flex>
              ) : (
                <Empty />
              )
            }
          />
        </Card>
      )}
    </>
  )
}
